import createFetchClient, { Middleware } from 'openapi-fetch';
import createClient from 'openapi-react-query';
import API_BASE_URL from 'src/constants';
import { paths } from 'src/generated/v1';
import logger from 'src/utils/logger';
import { attachErrorCode } from './apiErrorCode';
import { createUnauthenticatedResponse, getToken } from './authToken';

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const accessToken = getToken();
    if (!accessToken) {
      // Short-circuit: openapi-fetch skips the network call as soon as
      // onRequest returns a Response. Previously this branch assigned
      // window.location.href = '/login' *and then sent the request anyway*
      // with "Authorization: Bearer null" — a guaranteed 401, no re-entrancy
      // guard, and no "am I already on /login?" check (where the assignment is
      // a full document reload that aborts every other in-flight request,
      // including a login POST).
      //
      // Redirecting is not the transport layer's job. <Auth> subscribes to the
      // token store and navigates via the router.
      logger.warn('No access token found, request not sent');
      return createUnauthenticatedResponse();
    }
    request.headers.set('Authorization', `Bearer ${accessToken}`);
    return request;
  },
  async onResponse({ response }) {
    if (!response.ok) {
      logger.error(`API ${response.status} ${response.url}`);
    }
    // Copies the backend's `X-Error-Code` header into the JSON error body
    // (`error_code`): openapi-react-query mutations reject with the parsed body
    // only, the headers never reach the caller. See src/utils/inboundErrors.ts.
    return attachErrorCode(response);
  },
};

export const $apiFetchClient = createFetchClient<paths>({
  baseUrl: `${API_BASE_URL}portal/`,
});
$apiFetchClient.use(authMiddleware);

export const $api = createClient($apiFetchClient);

export const $publicApiFetchClient = createFetchClient<paths>({
  baseUrl: `${API_BASE_URL}portal/`,
});

export const $publicApi = createClient($publicApiFetchClient);
